import { useState, useEffect, useMemo } from 'react';

export default function EditableBigAtaTableModal({
    isOpen,
    title = 'Edit',
    subtitle,
    initialRows = [],
    preferredColumnOrder = [],
    isNumberColumn = (key) => false,
    onSave,
    onAutoSave,
    onCancel,
    autoSaveOnBackdrop = true,
    canDeleteRows = true,
    accountOptions,
    bigAtaConditionCodes,
}) {
    const uid = () => Math.random().toString(36).slice(2, 10);
    const withIds = (rows) => (rows ?? []).map((r) => (r && r.__id ? r : { __id: uid(), ...(r ?? {}) }));

    const [draftRows, setDraftRows] = useState([]);
    const [active, setActive] = useState(null);
    const [lastDeleted, setLastDeleted] = useState(null);

    const [suggestOpen, setSuggestOpen] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const accounts = useMemo(() => accountOptions ?? [], [accountOptions]);
    const conditionCodes = useMemo(() => bigAtaConditionCodes ?? [], [bigAtaConditionCodes]);

    const isAccountField = (key) => key === 'Trace' || key === 'TagAgency';

    const findExactAccountMatch = (value) => {
        const normalized = (value ?? '').toString().trim().toLowerCase();
        if (!normalized) return null;

        return accounts.find((acct) => (acct?.Name ?? '').toString().trim().toLowerCase() === normalized) ?? null;
    };

    const getFilteredAccounts = (query) => {
        const q = (query ?? '').toString().trim().toLowerCase();
        if (!q) return accounts.slice(0, 30);
        return accounts.filter((a) => (a?.Name ?? '').toLowerCase().includes(q)).slice(0, 30);
    };

    const selectAccount = (rowIndex, key, acct) => {
        const name = acct?.Name ?? '';
        const id = acct?.Id ?? '';

        setDraftRows((prev) => {
            const next = [...(prev ?? [])];
            const row = { ...(next[rowIndex] ?? {}) };

            row[key] = name;
            if (key === 'Trace') row.TraceId = id;
            if (key === 'TagAgency') row.TagAgencyId = id;

            next[rowIndex] = row;
            return next;
        });

        setFieldErrors((prev) => {
            const next = { ...prev };
            delete next[`${rowIndex}-${key}`];
            return next;
        });

        setSuggestOpen(null);
    };

    const selectConditionCode = (rowIndex, code) => {
        const name = code?.Name ?? '';
        const id = code?.Id ?? '';

        setDraftRows((prev) => {
            const next = [...(prev ?? [])];
            const row = { ...(next[rowIndex] ?? {}) };

            row.ConditionCode = name;
            row.ConditionCodeId = id;

            next[rowIndex] = row;
            return next;
        });

        setSuggestOpen(null);
    };

    useEffect(() => {
        if (!isOpen) return;
        setDraftRows(withIds(initialRows).map((r) => ({ ...r })));
        setActive(null);
        setLastDeleted(null);
        setSuggestOpen(null);
        setFieldErrors({});
    }, [isOpen, initialRows]);

    const columns = useMemo(() => {
        const keys = new Set();
        for (const r of draftRows) {
            Object.keys(r || {}).forEach((k) => {
                if (
                    k !== '__id' &&
                    k !== 'inventoryLineId' &&
                    k !== 'TraceId' &&
                    k !== 'TagAgencyId' &&
                    k !== 'ConditionCodeId'
                ) {
                    keys.add(k);
                }
            });
        }

        const base = [
            ...preferredColumnOrder.filter((k) => keys.has(k)),
            ...[...keys].filter((k) => !preferredColumnOrder.includes(k)),
        ];

        const withoutSerial = base.filter((k) => k !== 'SerialNumber');
        return base.includes('SerialNumber') ? [...withoutSerial, 'SerialNumber'] : withoutSerial;
    }, [draftRows, preferredColumnOrder]);

    const cellId = (r, k) => `cell-${r}-${k}`;

    useEffect(() => {
        if (!isOpen || !active) return;
        const el = document.getElementById(cellId(active.rowIndex, active.key));
        if (el) {
            el.focus();
            el.select?.();
        }
    }, [isOpen, active]);

    const undoDelete = () => {
        if (!lastDeleted) return;
        setDraftRows((prev) => {
            const next = [...(prev ?? [])];
            const idx = Math.max(0, Math.min(next.length, lastDeleted.index));
            next.splice(idx, 0, lastDeleted.row);
            return next;
        });
        setActive(null);
        setLastDeleted(null);
    };

    const getCleanedRows = () => (draftRows ?? []).map(({ __id, ...rest }) => rest);

    const commitSave = () => {
        const nextErrors = {};

        (draftRows ?? []).forEach((row, rowIndex) => {
            ['Trace', 'TagAgency'].forEach((key) => {
                const value = normalizeCompare(row?.[key]);
                const id = key === 'Trace' ? row?.TraceId : row?.TagAgencyId;
                const changed = didReferenceFieldChange(rowIndex, key);

                // untouched original value should not fail validation
                if (!changed) return;

                // changed but blank is allowed
                if (!value) return;

                // changed and text exists, require dropdown selection
                if (!id) {
                    nextErrors[`${rowIndex}-${key}`] = 'Please select a value from the dropdown or clear this field.';
                }
            });
        });

        setFieldErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) return;

        setLastDeleted(null);
        onSave?.(getCleanedRows());
    };

    const commitCancel = () => {
        setActive(null);
        setLastDeleted(null);
        setSuggestOpen(null);
        setFieldErrors({});
        onCancel?.();
    };

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                commitCancel();
                return;
            }
            const isUndo = (e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z');
            if (isUndo && lastDeleted) {
                e.preventDefault();
                undoDelete();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, lastDeleted]);

    const updateCell = (rowIndex, key, value) => {
        setDraftRows((prev) => {
            const next = [...(prev ?? [])];
            const row = { ...(next[rowIndex] ?? {}) };

            if (isNumberColumn(key)) {
                const n = Number(value);
                row[key] = Number.isFinite(n) ? n : 0;
            } else {
                const forceUppercase = ['PartNumber', 'Keyword', 'SerialNumber'];
                row[key] = forceUppercase.includes(key) ? (value ?? '').toString().toUpperCase() : value;

                if (key === 'Trace') row.TraceId = '';
                if (key === 'TagAgency') row.TagAgencyId = '';
            }

            next[rowIndex] = row;
            return next;
        });

        if (key === 'Trace' || key === 'TagAgency') {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[`${rowIndex}-${key}`];
                return next;
            });
        }
    };

    const deleteRow = (rowIndex) => {
        if (!canDeleteRows) return;
        setDraftRows((prev) => {
            const arr = prev ?? [];
            const row = arr[rowIndex];
            if (row) setLastDeleted({ row: { ...row }, index: rowIndex });
            return arr.filter((_, i) => i !== rowIndex);
        });
        setActive(null);
    };

    const handleKeyDown = (e, rowIndex, key) => {
        const colIndex = columns.findIndex((c) => c === key);
        const maxRow = draftRows.length - 1;

        const isAccountEditingField = isAccountField(key);
        const isThisSuggestOpen = suggestOpen?.rowIndex === rowIndex && suggestOpen?.key === key;

        if (
            isAccountEditingField &&
            isThisSuggestOpen &&
            ['Enter', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)
        ) {
            const row = draftRows?.[rowIndex] ?? {};
            const selectedId = key === 'Trace' ? row?.TraceId : row?.TagAgencyId;

            if (!selectedId) {
                e.preventDefault();
                const exactMatch = findExactAccountMatch(row?.[key] ?? '');
                if (exactMatch) {
                    selectAccount(rowIndex, key, exactMatch);
                }
                return;
            }
        }

        const move = (nextRow, nextCol) => {
            const r = Math.max(0, Math.min(maxRow, nextRow));
            const c = Math.max(0, Math.min(columns.length - 1, nextCol));
            setActive({ rowIndex: r, key: columns[c] });
        };

        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                move(rowIndex + 1, colIndex);
                break;
            case 'ArrowDown':
                move(rowIndex + 1, colIndex);
                break;
            case 'ArrowUp':
                move(rowIndex - 1, colIndex);
                break;
            case 'ArrowRight':
                move(rowIndex, colIndex + 1);
                break;
            case 'ArrowLeft':
                move(rowIndex, colIndex - 1);
                break;
            default:
                break;
        }
    };

    const commitAutoSave = () => {
        setLastDeleted(null);
        if (onAutoSave) return onAutoSave(getCleanedRows());
        onSave?.(getCleanedRows());
    };

    if (!isOpen) return null;

    const getColumnWidth = (key) => {
        if (key === 'SerialNumber') return 'w-28';
        if (key === 'Quantity') return 'w-20';
        if (key === 'ConditionCode') return 'w-32';
        if (key === 'Trace') return 'w-72';
        if (key === 'TagAgency') return 'w-72';
        return '';
    };

    const validateReferenceField = (rowIndex, key) => {
        if (!isAccountField(key)) return;

        const row = draftRows?.[rowIndex] ?? {};
        const value = normalizeCompare(row?.[key]);
        const id = key === 'Trace' ? row?.TraceId : row?.TagAgencyId;

        const changed = didReferenceFieldChange(rowIndex, key);

        // if user never changed this field, do not error
        if (!changed) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[`${rowIndex}-${key}`];
                return next;
            });
            return;
        }

        // if user changed it and then cleared it, that's allowed
        if (!value) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[`${rowIndex}-${key}`];
                return next;
            });
            return;
        }

        // user changed it and left text, now require a selected id
        if (!id) {
            setFieldErrors((prev) => ({
                ...prev,
                [`${rowIndex}-${key}`]: 'Please select a value from the dropdown or clear this field.',
            }));
        } else {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[`${rowIndex}-${key}`];
                return next;
            });
        }
    };

    const hasActiveErrors = Object.keys(fieldErrors ?? {}).length > 0;

    const normalizeCompare = (value) => (value ?? '').toString().trim();

    const didReferenceFieldChange = (rowIndex, key) => {
        const originalRow = withIds(initialRows)?.[rowIndex] ?? {};
        const currentRow = draftRows?.[rowIndex] ?? {};

        const originalValue = normalizeCompare(originalRow?.[key]);
        const currentValue = normalizeCompare(currentRow?.[key]);

        return originalValue !== currentValue;
    };

    const requiredFields = new Set(['ConditionCode', 'Trace', 'TagAgency', 'SerialNumber']);

    return (
        <div
            role='dialog'
            aria-modal='true'
            className='flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40'
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                    if (autoSaveOnBackdrop) commitAutoSave();
                    else commitCancel();
                }
            }}
        >
            <div
                className='bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] flex flex-col'
                onMouseDown={() => {
                    setSuggestOpen(null);
                }}
            >
                <div className='flex relative items-center p-4 border-b'>
                    <div className='flex flex-col flex-1 justify-center items-center text-center'>
                        <h1 className='text-3xl font-bold'>{title}</h1>

                        {subtitle ? <div className='text-sm text-gray-500'>{subtitle}</div> : null}
                    </div>
                    <button
                        type='button'
                        onClick={commitCancel}
                        aria-label='Close'
                        className='absolute right-4 top-1/2 px-2 text-xl text-gray-600 -translate-y-1/2 hover:text-black'
                    >
                        ✕
                    </button>
                </div>

                <div className='flex flex-col flex-1 p-4 min-h-0'>
                    <div className='flex gap-3 items-center mb-3'>
                        {lastDeleted ? (
                            <button type='button' onClick={undoDelete} className='px-3 py-1 rounded border'>
                                Undo delete
                            </button>
                        ) : null}

                        <div className='text-sm text-gray-500'>Rows: {draftRows.length}</div>
                    </div>

                    <div className='overflow-auto flex-1 rounded border border-gray-300'>
                        <table className='w-full text-sm border-collapse'>
                            <thead className='px-2 text-white bg-blue-primary'>
                                <tr className='border-b border-blue-secondary/40'>
                                    {columns.map((k, idx) => (
                                        <th
                                            key={k}
                                            className={`${idx === 0 ? 'pl-3' : ''}
                                            ${getColumnWidth(k)}
                                            sticky top-0 z-10 text-left font-semibold py-2 pr-2 bg-blue-primary`}
                                        >
                                            {k}
                                            {requiredFields.has(k) && <span className='ml-1'>*</span>}
                                        </th>
                                    ))}
                                    {canDeleteRows ? <th className='sticky top-0 z-10 py-2 bg-blue-primary' /> : null}
                                </tr>
                            </thead>

                            <tbody>
                                {draftRows.map((row, rowIndex) => (
                                    <tr
                                        key={row.__id}
                                        className='h-10 border-b border-gray-400 odd:bg-gray-200 even:bg-gray-50'
                                    >
                                        {columns.map((k, colIdx) => {
                                            const value = row?.[k] ?? '';
                                            const isActive = active?.rowIndex === rowIndex && active?.key === k;
                                            const isAccountSuggest = k === 'Trace' || k === 'TagAgency';
                                            const isConditionSelect = k === 'ConditionCode';
                                            const showThisSuggest =
                                                suggestOpen?.rowIndex === rowIndex && suggestOpen?.key === k;
                                            const filtered = isAccountSuggest ? getFilteredAccounts(value) : [];

                                            const hasError = Boolean(fieldErrors?.[`${rowIndex}-${k}`]);

                                            return (
                                                <td
                                                    key={k}
                                                    className={`${colIdx === 0 ? 'pl-3' : ''} py-1 pr-2 align-middle`}
                                                >
                                                    <div className='relative'>
                                                        {isConditionSelect ? (
                                                            <select
                                                                id={cellId(rowIndex, k)}
                                                                className={[
                                                                    'w-full bg-transparent',
                                                                    'px-2 py-1',
                                                                    'outline-none',
                                                                    'border border-transparent rounded',
                                                                    isActive
                                                                        ? 'bg-white border-blue-300 ring-2 ring-blue-300'
                                                                        : 'hover:bg-white/60',
                                                                ].join(' ')}
                                                                value={String(value)}
                                                                onChange={(e) => {
                                                                    const selectedName = e.target.value;
                                                                    const selectedCode = conditionCodes.find(
                                                                        (c) => c.Name === selectedName
                                                                    );

                                                                    if (selectedCode) {
                                                                        selectConditionCode(rowIndex, selectedCode);
                                                                    } else {
                                                                        updateCell(rowIndex, k, '');
                                                                    }
                                                                }}
                                                                onFocus={() => {
                                                                    setActive({ rowIndex, key: k });
                                                                }}
                                                                onKeyDown={(e) => handleKeyDown(e, rowIndex, k)}
                                                            >
                                                                <option value='' disabled>
                                                                    Select Condition
                                                                </option>
                                                                {conditionCodes.map((code) => (
                                                                    <option key={code.Id} value={code.Name}>
                                                                        {code.Name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <>
                                                                <input
                                                                    id={cellId(rowIndex, k)}
                                                                    className={[
                                                                        'w-full min-w-0 bg-transparent',
                                                                        'px-2 py-1',
                                                                        'outline-none',
                                                                        'border rounded',
                                                                        'overflow-hidden text-ellipsis',
                                                                        hasError
                                                                            ? 'bg-white border-red-500 ring-2 ring-red-300'
                                                                            : isActive
                                                                            ? 'bg-white border-blue-300 ring-2 ring-blue-300'
                                                                            : 'border-transparent hover:bg-white/60',
                                                                    ].join(' ')}
                                                                    value={String(value)}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        updateCell(rowIndex, k, v);
                                                                        if (isAccountSuggest)
                                                                            setSuggestOpen({ rowIndex, key: k });
                                                                    }}
                                                                    onFocus={() => {
                                                                        setActive({ rowIndex, key: k });
                                                                        if (isAccountSuggest)
                                                                            setSuggestOpen({ rowIndex, key: k });
                                                                    }}
                                                                    onBlur={() => {
                                                                        if (!isAccountSuggest) return;

                                                                        setTimeout(() => {
                                                                            validateReferenceField(rowIndex, k);
                                                                        }, 150);
                                                                    }}
                                                                    onMouseDown={(e) => {
                                                                        if (isAccountSuggest) e.stopPropagation();
                                                                    }}
                                                                    onKeyDown={(e) => handleKeyDown(e, rowIndex, k)}
                                                                    inputMode={isNumberColumn(k) ? 'numeric' : undefined}
                                                                    autoComplete='off'
                                                                    autoCorrect='off'
                                                                    autoCapitalize='off'
                                                                    spellCheck={false}
                                                                />
                                                                {fieldErrors?.[`${rowIndex}-${k}`] && (
                                                                    <div className='mt-1 max-w-full text-xs text-red-600 whitespace-normal break-words'>
                                                                        {fieldErrors[`${rowIndex}-${k}`]}
                                                                    </div>
                                                                )}

                                                                {isAccountSuggest &&
                                                                showThisSuggest &&
                                                                filtered.length > 0 ? (
                                                                    <div
                                                                        className='overflow-auto absolute z-50 mt-1 w-full max-h-56 bg-white rounded border border-gray-200 shadow'
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                    >
                                                                        {filtered.map((acct) => (
                                                                            <button
                                                                                key={acct.Id}
                                                                                type='button'
                                                                                className='block px-2 py-2 w-full text-left hover:bg-gray-100'
                                                                                onMouseDown={(e) => {
                                                                                    e.preventDefault();
                                                                                    selectAccount(rowIndex, k, acct);
                                                                                }}
                                                                            >
                                                                                {acct.Name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}

                                        {canDeleteRows ? (
                                            <td className='py-1 pr-2 text-right align-middle'>
                                                <button
                                                    type='button'
                                                    onClick={() => deleteRow(rowIndex)}
                                                    className='flex justify-center items-center w-6 h-6 text-red-600 rounded transition hover:bg-red-50 hover:text-red-700'
                                                >
                                                    X
                                                </button>
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}

                                {draftRows.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={columns.length + (canDeleteRows ? 1 : 0)}
                                            className='p-3 text-center text-gray-500'
                                        >
                                            No data found.
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className='flex justify-between items-center p-4 border-t'>
                    <div className='text-[11px] text-gray-500 italic'>
                        *These values if changed will be written back into AvSight.
                    </div>

                    <div className='flex gap-3 items-center'>
                        <button type='button' onClick={commitCancel} className='px-4 py-2 rounded border'>
                            Cancel
                        </button>

                        <button
                            type='button'
                            onClick={commitSave}
                            disabled={hasActiveErrors}
                            className={`px-4 py-1.5 text-white rounded ${
                                hasActiveErrors ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-primary hover:bg-blue-700'
                            }`}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
